import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../shared/contexts/AuthContext';
import { CWYLoader } from '../common/CWYLoader';

interface SmartRedirectProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

const SmartRedirect: React.FC<SmartRedirectProps> = ({ 
  children, 
  fallbackPath = '/login' 
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate(fallbackPath);
    } else if (!loading && user) {
      // Redirect based on user role and permissions
      if (user.permissionRole === 'admin') {
        navigate('/app', { replace: true });
      } else {
        // For non-admin users, check if they have dashboard access
        const hasDashboardAccess = user.pageAccess && user.pageAccess.includes('/app');
        if (hasDashboardAccess) {
          navigate('/app', { replace: true });
        } else {
          // Redirect to their first accessible page
          const accessiblePages = user.pageAccess || [];
          if (accessiblePages.length > 0) {
            navigate(accessiblePages[0], { replace: true });
          } else {
            // Fallback to home page if no specific access
            navigate('/app', { replace: true });
          }
        }
      }
    }
  }, [user, loading, navigate, fallbackPath]);

  if (loading) {
    return <CWYLoader fullScreen size={100} />;
  }

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          bgcolor: 'background.default',
        }}
      >
        <CWYLoader size={72} />
        <Typography variant="body2" color="text.secondary">
          Redirecting…
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default SmartRedirect; 